import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import { triggerCsvDownload, triggerTextDownload, dateStamp } from '../../services/downloadHelpers';
import Breadcrumb from '../../components/ui/Breadcrumb';
import AuditorDashboard from './components/AuditorDashboard';
import ReportsViewer from './components/ReportsViewer';
import AuditTrailViewer from './components/AuditTrailViewer';
import DocumentViewer from './components/DocumentViewer';
import ExportTools from './components/ExportTools';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AuditorPortal = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [selectedReport, setSelectedReport] = useState(null);
  const [sessionTime, setSessionTime] = useState(0);
  const [accessLog, setAccessLog] = useState([]);

  // Mock audit data
  const auditMetrics = {
    totalReports: 127,
    complianceScore: 94.2,
    pendingReviews: 8,
    completedAssessments: 42,
    dataIntegrityScore: 98.7,
    lastUpdate: '2025-01-09T10:30:00Z'
  };

  const reportsData = [
    {
      id: 'rpt-001',
      name: 'Q4 2024 EU Pay Transparency Report',
      type: 'eu-directive-standard',
      period: '2024-q4',
      status: 'verified',
      createdAt: '2024-10-08T10:30:00Z',
      verifiedBy: 'System Validation',
      checksum: 'sha256:a1b2c3d4e5f6...',
      fileSize: 2457600,
      completeness: 98.5,
      dataPoints: 2847,
      department: 'Global',
      submittedBy: 'sarah.johnson@clearequal.com'
    },
    {
      id: 'rpt-002',
      name: 'Germany Entgelttransparenz Report H2 2024',
      type: 'germany-entgelttransparenz',
      period: '2024-h2',
      status: 'verified',
      createdAt: '2024-09-15T14:22:00Z',
      verifiedBy: 'External Auditor',
      checksum: 'sha256:b2c3d4e5f6g7...',
      fileSize: 1843200,
      completeness: 96.8,
      dataPoints: 1923,
      department: 'Germany Operations',
      submittedBy: 'michael.weber@clearequal.com'
    },
    {
      id: 'rpt-003',
      name: 'Annual Pay Equity Analysis 2023',
      type: 'equity-analysis',
      period: '2023-annual',
      status: 'under-review',
      createdAt: '2024-01-31T09:15:00Z',
      verifiedBy: null,
      checksum: 'sha256:c3d4e5f6g7h8...',
      fileSize: 3276800,
      completeness: 99.2,
      dataPoints: 4156,
      department: 'EU Operations',
      submittedBy: 'admin@clearequal.com'
    }
  ];

  const auditTrail = [
    {
      id: 'audit-001',
      timestamp: '2025-01-09T10:15:00Z',
      action: 'REPORT_ACCESSED',
      user: 'auditor@clearequal.com',
      resource: 'Q4 2024 EU Pay Transparency Report',
      ipAddress: '192.168.1.200',
      userAgent: 'Chrome/120.0.0.0',
      details: 'Full report accessed for compliance review',
      integrity: 'VERIFIED'
    },
    {
      id: 'audit-002',
      timestamp: '2025-01-09T09:45:00Z',
      action: 'DATA_EXPORTED',
      user: 'external.auditor@clearequal.com',
      resource: 'Germany Operations Dataset',
      ipAddress: '203.0.113.45',
      userAgent: 'Firefox/121.0',
      details: 'CSV export generated with checksums',
      integrity: 'VERIFIED'
    },
    {
      id: 'audit-003',
      timestamp: '2025-01-09T09:30:00Z',
      action: 'REPORT_VERIFIED',
      user: 'compliance.system@clearequal.com',
      resource: 'H2 2024 Transparency Report',
      ipAddress: '192.168.1.100',
      userAgent: 'System/1.0',
      details: 'Automated verification completed successfully',
      integrity: 'SYSTEM_VERIFIED'
    }
  ];

  // Session management
  useEffect(() => {
    const timer = setInterval(() => {
      setSessionTime(prev => prev + 1);
    }, 1000);

    // Auto-logout after 30 minutes of inactivity
    const inactivityTimer = setTimeout(() => {
      alert('Session expired due to inactivity. Please log in again.');
      window.location.href = '/login';
    }, 1800000); // 30 minutes

    return () => {
      clearInterval(timer);
      clearTimeout(inactivityTimer);
    };
  }, []);

  // Log access
  useEffect(() => {
    const logEntry = {
      timestamp: new Date()?.toISOString(),
      action: 'PORTAL_ACCESSED',
      section: activeSection,
      sessionId: 'sess-' + Date.now()
    };
    setAccessLog(prev => [logEntry, ...prev?.slice(0, 49)]);
  }, [activeSection]);

  const handleExportData = (data, format) => {
    const fmt = (format || 'csv').toLowerCase();
    if (fmt === 'csv') {
      triggerCsvDownload(`ClearEqual_AuditExport_${dateStamp()}.csv`, data || []);
    } else {
      const lines = [
        '='.repeat(72),
        'CLEAREQUAL, AUDITOR PORTAL EXPORT',
        '='.repeat(72),
        '',
        `Format requested: ${format}`,
        `Records: ${data?.length || 0}`,
        `Export generated: ${dateStamp()}`,
        '',
        JSON.stringify(data || [], null, 2),
      ].join('\n');
      triggerTextDownload(`ClearEqual_AuditExport_${dateStamp()}.txt`, lines);
    }
  };

  const handleReportView = (report) => {
    setSelectedReport(report);
    const logEntry = {
      timestamp: new Date()?.toISOString(),
      action: 'REPORT_VIEWED',
      resource: report?.name,
      integrity: 'VERIFIED'
    };
    setAccessLog(prev => [logEntry, ...prev?.slice(0, 49)]);
  };

  const formatSessionTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/analytics-dashboard' },
    { label: 'Auditor Portal', path: '/auditor-portal' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb items={breadcrumbItems} />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={24} className="text-accent" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{t('auditor.pageTitle')}</h1>
                <p className="text-muted-foreground">
                  Secure read-only access for external compliance auditing
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Session Info */}
              <div className="bg-muted/50 rounded-lg px-3 py-2 text-sm text-muted-foreground">
                Session: {formatSessionTime(sessionTime)}
              </div>
              
              {/* Navigation Tabs */}
              <div className="flex items-center bg-muted rounded-lg p-1">
                <button
                  onClick={() => setActiveSection('dashboard')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                    activeSection === 'dashboard' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setActiveSection('reports')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                    activeSection === 'reports' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Reports
                </button>
                <button
                  onClick={() => setActiveSection('trail')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                    activeSection === 'trail' ?'bg-card text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Audit Trail
                </button>
                <button
                  onClick={() => setActiveSection('documents')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                    activeSection === 'documents' 
                      ? 'bg-card text-foreground shadow-soft' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Documents
                </button>
              </div>
              
              <Button variant="outline" iconName="Download" iconPosition="left">
                Export Package
              </Button>
            </div>
          </div>

          {/* Security Banner */}
          <div className="bg-gradient-to-r from-accent/10 to-primary/10 border border-accent/20 rounded-lg p-4 mb-8">
            <div className="flex items-center space-x-3">
              <Icon name="ShieldCheck" size={20} className="text-accent" />
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">
                  Secure Audit Environment Active
                </p>
                <p className="text-xs text-muted-foreground">
                  All access logged • Read-only permissions • Session monitoring enabled • Data integrity verified
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs text-muted-foreground">Live</span>
              </div>
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {activeSection === 'dashboard' && (
              <AuditorDashboard 
                metrics={auditMetrics}
                recentActivity={auditTrail?.slice(0, 5)}
                onNavigate={setActiveSection}
              />
            )}

            {activeSection === 'reports' && (
              <ReportsViewer 
                reports={reportsData}
                onViewReport={handleReportView}
                selectedReport={selectedReport}
              />
            )}

            {activeSection === 'trail' && (
              <AuditTrailViewer 
                auditLogs={auditTrail}
                onExportData={handleExportData}
                sessionLog={accessLog}
              />
            )}

            {activeSection === 'documents' && (
              <DocumentViewer 
                reports={reportsData}
                onViewDocument={handleReportView}
              />
            )}
          </div>

          {/* Export Tools Footer */}
          <div className="mt-12 pt-8 border-t border-border">
            <ExportTools 
              onExport={handleExportData}
              availableFormats={['pdf', 'csv', 'xlsx', 'json']}
              includeIntegrityChecks={true}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AuditorPortal;